/**
 * AdminPage — minimal admin dashboard.
 *
 * Accessible only to users with the `admin: true` custom claim.
 * Reached via the "Admin" link shown in the sidebar when the user is an admin.
 *
 * Features:
 *   • List all Firebase Auth users with their override status
 *   • Grant / update / revoke adminOverrides (manual access grants)
 *   • Toggle the admin custom claim on other users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { theme } from '../theme';
import { apiGet, apiPost, apiDelete } from '../api';
import { AuthShell } from './LoginPage';
import { useAuth } from '../context/AuthContext';

export default function AdminPage({ onClose }) {
  const { user } = useAuth();
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [nextPage, setNextPage]   = useState(null);
  const [grantUid, setGrantUid]   = useState(null); // uid of user being granted override
  const [grantForm, setGrantForm] = useState({ expiresAt: '', reason: '' });
  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState('');

  const loadUsers = useCallback(async (pageToken) => {
    setLoading(true); setError('');
    try {
      const url = pageToken ? `/admin/users?pageToken=${encodeURIComponent(pageToken)}` : '/admin/users';
      const data = await apiGet(url);
      setUsers(prev => pageToken ? [...prev, ...data.users] : data.users);
      setNextPage(data.nextPageToken || null);
    } catch (err) {
      setError(err.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  async function handleGrantOverride(e) {
    e.preventDefault();
    setBusy(true); setMsg('');
    try {
      await apiPost(`/admin/overrides/${grantUid}`, grantForm);
      setMsg('Override granted.');
      setGrantUid(null);
      loadUsers();
    } catch (err) {
      setMsg(err.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRevokeOverride(uid) {
    if (!window.confirm('Revoke access override for this user?')) return;
    setBusy(true); setMsg('');
    try {
      await apiDelete(`/admin/overrides/${uid}`);
      setMsg('Override revoked.');
      loadUsers();
    } catch (err) {
      setMsg(err.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleAdmin(uid, currentlyAdmin) {
    const action = currentlyAdmin ? 'Remove admin from' : 'Make admin';
    if (!window.confirm(`${action} this user?`)) return;
    setBusy(true); setMsg('');
    try {
      await apiPost(`/admin/set-admin-claim/${uid}`, { admin: !currentlyAdmin });
      setMsg(`Admin claim ${currentlyAdmin ? 'removed' : 'granted'}.`);
      loadUsers();
    } catch (err) {
      setMsg(err.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  // Default expiry = 30 days from now
  function defaultExpiry() {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM" for datetime-local
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 12, width: '100%', maxWidth: 1040,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>Admin Dashboard</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: theme.textMuted,
            cursor: 'pointer', fontSize: 20, lineHeight: 1,
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {msg && <p style={{ color: theme.success, fontSize: 13, marginBottom: 12 }}>{msg}</p>}
          {error && <p style={{ color: theme.danger, fontSize: 13, marginBottom: 12 }}>{error}</p>}

          {loading && !users.length
            ? <p style={{ color: theme.textMuted, fontSize: 14 }}>Loading users…</p>
            : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  {['Email', 'Verified', 'Admin', 'Subscription', 'Override expires', 'Actions'].map(h => (
                    <th key={h} style={{
                      textAlign: 'left', padding: '6px 8px',
                      color: theme.textMuted, fontWeight: 600, whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isAdmin   = !!u.customClaims?.admin;
                  const isSelf    = u.uid === user?.uid;
                  const override  = u.override;
                  const overrideExp = override?.expiresAt?.toDate
                    ? override.expiresAt.toDate()
                    : override?.expiresAt ? new Date(override.expiresAt._seconds * 1000) : null;

                  return (
                    <tr key={u.uid} style={{ borderBottom: `1px solid ${theme.border}20` }}>
                      <td style={{ padding: '8px 8px', color: theme.text }}>
                        {u.email}
                        {isSelf && <span style={{ color: theme.accent, fontSize: 11, marginLeft: 6 }}>(you)</span>}
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        <span style={{ color: u.emailVerified ? theme.success : theme.warning, fontSize: 12 }}>
                          {u.emailVerified ? '✓' : '✗'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        <span style={{ color: isAdmin ? theme.accent : theme.textMuted, fontSize: 12 }}>
                          {isAdmin ? '✓ Admin' : '—'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        <SubBadge subscription={u.subscription} />
                      </td>
                      <td style={{ padding: '8px 8px', color: override ? theme.success : theme.textMuted, fontSize: 12 }}>
                        {overrideExp
                          ? overrideExp.toLocaleDateString()
                          : '—'}
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {/* Grant / update override */}
                          <Btn onClick={() => {
                            setGrantUid(u.uid);
                            setGrantForm({ expiresAt: defaultExpiry(), reason: override?.reason || '' });
                            setMsg('');
                          }}>
                            {override ? 'Extend' : 'Grant access'}
                          </Btn>

                          {/* Revoke override */}
                          {override && (
                            <Btn danger onClick={() => handleRevokeOverride(u.uid)} disabled={busy}>
                              Revoke
                            </Btn>
                          )}

                          {/* Toggle admin */}
                          {!isSelf && (
                            <Btn onClick={() => handleToggleAdmin(u.uid, isAdmin)} disabled={busy}>
                              {isAdmin ? 'Remove admin' : 'Make admin'}
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {nextPage && (
            <button onClick={() => loadUsers(nextPage)} disabled={loading}
              style={{ marginTop: 12, ...btnBase }}>
              Load more
            </button>
          )}
        </div>
      </div>

      {/* Grant override modal */}
      {grantUid && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
        }}>
          <form onSubmit={handleGrantOverride} style={{
            background: theme.surface, border: `1px solid ${theme.border}`,
            borderRadius: 10, padding: 24, width: 360,
            display: 'flex', flexDirection: 'column', gap: 14,
          }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>Grant Access Override</h3>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: theme.textMuted }}>Expires at</span>
              <input type="datetime-local" required
                value={grantForm.expiresAt}
                onChange={e => setGrantForm(f => ({ ...f, expiresAt: e.target.value }))}
                style={inputStyle} />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: theme.textMuted }}>Reason (optional)</span>
              <input type="text" placeholder="e.g. Pilot customer, comped account…"
                value={grantForm.reason}
                onChange={e => setGrantForm(f => ({ ...f, reason: e.target.value }))}
                style={inputStyle} />
            </label>

            {msg && <p style={{ color: theme.danger, fontSize: 12 }}>{msg}</p>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setGrantUid(null)}
                style={{ ...btnBase, color: theme.textMuted }}>Cancel</button>
              <button type="submit" disabled={busy}
                style={{ ...btnBase, background: theme.accent, color: '#fff' }}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Micro-components ─────────────────────────────────────────────────────────

const SUB_STYLES = {
  active:     { dot: '#22c55e', label: 'Active' },
  trialing:   { dot: '#3b82f6', label: 'Trial'  },
  canceled:   { dot: '#ef4444', label: 'Canceled' },
  past_due:   { dot: '#f97316', label: 'Past due' },
  incomplete: { dot: '#eab308', label: 'Incomplete' },
  incomplete_expired: { dot: '#ef4444', label: 'Expired' },
  unpaid:     { dot: '#f97316', label: 'Unpaid' },
};

function SubBadge({ subscription }) {
  if (!subscription || !subscription.status) {
    return <span style={{ color: theme.textMuted, fontSize: 12 }}>—</span>;
  }
  const { status, trialEnd, cancelAtPeriodEnd, currentPeriodEnd } = subscription;
  const style = SUB_STYLES[status] || { dot: theme.textMuted, label: status };

  let detail = '';
  if (status === 'trialing' && trialEnd) {
    detail = ` · ends ${new Date(trialEnd).toLocaleDateString()}`;
  } else if (cancelAtPeriodEnd && currentPeriodEnd) {
    detail = ` · cancels ${new Date(currentPeriodEnd).toLocaleDateString()}`;
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: style.dot, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ color: theme.text }}>{style.label}{detail}</span>
    </span>
  );
}

const btnBase = {
  background: 'none', border: `1px solid ${theme.border}`, borderRadius: 5,
  color: theme.text, cursor: 'pointer', fontSize: 12, padding: '4px 10px',
};

function Btn({ danger, children, ...props }) {
  return (
    <button {...props} style={{
      ...btnBase,
      color: danger ? theme.danger : theme.text,
      borderColor: danger ? theme.danger + '66' : theme.border,
      opacity: props.disabled ? 0.5 : 1,
      cursor: props.disabled ? 'not-allowed' : 'pointer',
    }}>
      {children}
    </button>
  );
}

const inputStyle = {
  background: theme.bg, border: `1px solid ${theme.border}`,
  borderRadius: 6, padding: '7px 10px', color: theme.text, fontSize: 13, outline: 'none',
};
